import { useState, useEffect } from "react";
import { SettingsCategory, Subcategory } from "./constants/settingsStructure";
import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";

interface CategoryLayoutProps {
  category: SettingsCategory;
}

const CategoryLayout = ({ category }: CategoryLayoutProps) => {
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory>(category.subcategories[0]);

  // Reset to first subcategory when category changes
  useEffect(() => {
    setSelectedSubcategory(category.subcategories[0]);
  }, [category]);

  const DetailComponent = selectedSubcategory?.component;

  return (
    <div className="flex h-full">
      {/* Middle Panel - Subcategories List */}
      <div className="w-80 border-r border-border overflow-y-auto bg-card/50 dark:bg-card">
        <div className="p-4 border-b border-border h-[60px] flex items-center">
          <h3 className="font-semibold text-foreground">{category.label}</h3>
        </div>
        <ul>
          {category.subcategories.map((sub) => (
            <li
              key={sub.id}
              onClick={() => setSelectedSubcategory(sub)}
              className={cn(
                "p-4 cursor-pointer hover:bg-accent/50 transition-colors border-b border-border relative",
                selectedSubcategory?.id === sub.id && "bg-blue-50 dark:bg-blue-900/20 before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-blue-500"
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-foreground">{sub.label}</div>
                <div className="flex items-center gap-1.5">
                  {sub.required && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      Required
                    </Badge>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Right Panel - Detail Component */}
      <div className="flex-1 overflow-y-auto bg-card/30 dark:bg-card/50">
        {DetailComponent ? (
          <DetailComponent subcategory={selectedSubcategory} categoryId={category.id} />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">Select a setting to view details</div>
        )}
      </div>
    </div>
  );
};

export default CategoryLayout;
